import { api, types } from "@/lib/openapi";
import { onMounted, ref, watch } from "vue";
import { useStekManager } from "@/composables/stek-manager";
import { defineStore, storeToRefs } from "pinia";
import { type SyncReturn, useSyncStore } from "@/stores/sync-store";
import { syncChannel } from "@/lib/broadcast-channels";
import { type DomainInTree } from "@/stores/domains-store";
import { type ProductInTree } from "@/stores/products-store";
import { type ProcessInTree } from "@/stores/processes-store";

export type Worktree = types.Worktree;

// The worktree store is primarily used for the lefthand stekz-tree component.
// However, it stores quite a bit of information which is useful for other pages
// that want to use some meta info (i.e. what is the current domain identity)
// without having those pages to actually fetch the entire entity from the api.
export const useWorktreeStore = defineStore("worktree", () => {
    const worktree = ref<Worktree>();
    const isFetching = ref(false);
    const { stek } = useStekManager();

    const controller = ref(new AbortController());

    const sync = async () => {
        // Abort any previously running requests and create a new controller
        controller.value.abort();
        controller.value = new AbortController();

        isFetching.value = true;
        const { response, data } =
            await api.startWorkspaceWorktreeMgtGetWorktreeStartPost({
                body: {
                    workspace_id: "default",
                },
                signal: controller.value.signal,
            });

        if (!response.ok) {
            isFetching.value = false;
            return;
        }

        worktree.value = data;
        isFetching.value = false;
    };

    watch(
        () => stek.value?.identity,
        () => {
            if (stek.value) {
                sync();
            } else {
                worktree.value = undefined;
            }
        }
    );

    function findDomainByIdentity(identity: string): DomainInTree | undefined {
        let domain: DomainInTree | undefined;
        if (!worktree.value) {
            return;
        }

        for (const d of worktree.value.domains) {
            if (d.identity === identity) {
                domain = d;
                break;
            }
        }

        return domain;
    }

    function findProductByIdentity(
        identity: string
    ): ProductInTree | undefined {
        let product: ProductInTree | undefined;
        if (!worktree.value) {
            return;
        }

        for (const d of worktree.value.domains) {
            for (const p of d.products) {
                if (p.identity === identity) {
                    product = p;
                    break;
                }
            }
        }

        return product;
    }

    function findProcessByIdentity(
        identity: string
    ): ProcessInTree | undefined {
        let process: ProcessInTree | undefined;
        if (!worktree.value) {
            return;
        }

        for (const d of worktree.value.domains) {
            for (const p of d.products) {
                for (const pr of p.processes) {
                    if (pr.identity === identity) {
                        process = pr;
                        break;
                    }
                }
            }
        }

        return process;
    }

    onMounted(() => {
        const syncStore = useSyncStore();
        const { sync } = useWorktreeStore();

        // Listen to changes on the current store.
        syncStore.$onAction(({ name, after }) => {
            if (name === "sync") {
                after((fullySyncedIdentities: SyncReturn) => {
                    if (
                        fullySyncedIdentities.some(
                            (identity) =>
                                identity.attribute === "name" ||
                                identity.attribute === "description"
                        )
                    ) {
                        sync();
                    }
                });
            }
        });

        // Listen to changes of stores in other tabs.
        syncChannel.addEventListener(
            "message",
            ({ data: { message, identity } }) => {
                if (
                    message === "synced" &&
                    (identity.attribute === "name" ||
                        identity.attribute === "description")
                ) {
                    sync();
                }
            }
        );
    });

    return {
        worktree,
        sync,
        isFetching,
        findDomainByIdentity,
        findProductByIdentity,
        findProcessByIdentity,
    };
});

export const useWorktreeManager = () => storeToRefs(useWorktreeStore());
